import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Row,
  useOverlay,
  Separator,
  InfiniteScroller,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import { UserContext } from '../UserContext'
import {
  getUserCompletedOrdinances,
  getUserCompletedOrdinancesCount,
  updateUserOrdinancesHiddenFlag,
} from '../../../services/ordinanceService'
import { PermissionsContext } from '../../app/PermissionsContext'
import UserOrdinancesResponsiveTable from './UserOrdinancesResponsiveTable'
import useNotifications from '../../../hooks/useNotifications'

export default function UserCompletedOrdinances() {
  usePageView({ page_detail: 'DMConsole:UserCompletedOrdinances' })
  const { error } = useNotifications()
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.completedOrdinances'))
  const userContext = useContext(UserContext)
  const { currentUser } = userContext
  const { contributorId, metadata, dmcUser } = currentUser
  const { cisId } = dmcUser
  const { isAuthorized } = useContext(PermissionsContext)

  const [totalCompletedOrdinancesCount, setTotalCompletedOrdinancesCount] = useState()
  const [completedOrdinances, setCompletedOrdinances] = useState()
  const [failedCtrUpdates, setFailedCtrUpdates] = useState([])
  // rows selected in the ResponsiveCheckboxTable
  const [selectedRows, setSelectedRows] = useState([])
  // UserOrdinance offset to return data from the API
  const [offset, setOffSet] = useState(0)
  // API limit per request
  const ORDINANCE_API_LIMIT = 100
  // We will always get results grouped by principal personId
  const groupByPrincipalPersonId = true

  const [userId, setUserId] = useState(null)
  const [idType, setIdType] = useState(null)

  // Overlay to display list of ctrIds that failed to have their "hiddenFromUser" flag updated
  const ctrFailedUpdateOverlay = useOverlay()

  // Setting up userId and IdType once per user
  useEffect(() => {
    if (cisId != null && cisId !== undefined) {
      setIdType(() => 'cis')
      setUserId(() => cisId.split('.')[2])
    } else if (contributorId != null && contributorId !== undefined) {
      setUserId(() => contributorId)
    } else {
      // try mrn if cisId and contributorId are not defined
      metadata.items.forEach((item) => {
        if (item.field === 'mrn') {
          setIdType(() => 'mrn')
          setUserId(item.value ? item.value : item.values[0].value)
        }
      })
    }
  }, [cisId, contributorId, metadata.items])

  // Get total completed ordinance count - once per user
  useEffect(() => {
    const fetchUserCompletedOrdinancesCount = async () => {
      const response = await getUserCompletedOrdinancesCount(userId, idType, groupByPrincipalPersonId)
      if (response.status === 200) {
        if (!response.data) {
          error(
            t(
              'user.errors.userHasNoCisId',
              "Could not get completed ordinances because there is no CIS ID associated with this user's MRN"
            )
          )
        }
        setTotalCompletedOrdinancesCount(response.data ? response.data.count : response.data)
      }
    }
    if (userId != null && userId !== undefined) fetchUserCompletedOrdinancesCount()
  }, [userId, idType, groupByPrincipalPersonId, error, t])

  // Get the totalCompletedOrdinances
  const fetchUserCompletedOrdinances = useCallback(
    async (currentOffset = 0) => {
      const responseDetail = await getUserCompletedOrdinances(
        userId,
        idType,
        currentOffset,
        ORDINANCE_API_LIMIT,
        groupByPrincipalPersonId
      )
      setOffSet(currentOffset + ORDINANCE_API_LIMIT)
      if (responseDetail.status === 200 && responseDetail.data.length > 0) {
        setCompletedOrdinances((previousSet) => {
          if (previousSet) {
            return [...previousSet, ...responseDetail.data]
          }
          return responseDetail.data
        })
        return true
      }
      return false
    },
    [userId, idType, groupByPrincipalPersonId]
  )

  const reFetch = async () => {
    await getUserCompletedOrdinances(userId, idType, offset, ORDINANCE_API_LIMIT, groupByPrincipalPersonId).then(
      (responseDetail) => {
        if (responseDetail.status === 200) {
          setCompletedOrdinances(() => [...responseDetail.data])
        } else {
          error(t('user.errors.somethingWentWrong', 'Something went wrong. Refresh the page.'))
        }
      }
    )
  }

  const updateHiddenFromUserFlag = () => {
    const updatePromises = []
    const ctrsFailedUpdate = []
    let someFailed = false
    selectedRows.forEach((row) => {
      const ctrId = row.split('*')[2]
      const hiddenFromUser = row.split('*')[3]
      updatePromises.push(updateUserOrdinancesHiddenFlag(userId, idType, ctrId, hiddenFromUser))
    })
    Promise.allSettled(updatePromises).then((responses) => {
      responses.forEach((response) => {
        if (response.value.data?.length > 0) {
          someFailed = true
          const urlSplit = response.value.config.url.split('/')
          ctrsFailedUpdate.push(`${urlSplit[urlSplit.length - 1]}: ${response.value.data}`)
        }
      })
      setSelectedRows([])
      if (someFailed) {
        setFailedCtrUpdates(() => [...ctrsFailedUpdate])
        error(t('ordinances.users.someFailedToUpdate', 'Some CTR-IDs failed to update "Hidden from User" flag.'))
      }
      reFetch()
    })
  }

  const failedUpdateOverlay = () => {
    return (
      <>
        <Button variant="contained" emphasis="medium" onClick={ctrFailedUpdateOverlay.handleClick}>
          {t('ordinances.users.showFailedToUpdate', 'Show Failed CtrIds')}
        </Button>
        <DialogOverlay
          title={t('ordinances.users.failedToUpdate', 'CTR-IDs update failed')}
          {...ctrFailedUpdateOverlay}
        >
          {failedCtrUpdates.map((ctrId) => (
            <React.Fragment key={ctrId}>
              {ctrId}
              <br />
            </React.Fragment>
          ))}

          <DialogOverlayFooter>
            <DialogOverlayActions>
              <Button onClick={ctrFailedUpdateOverlay.close}>{t('action.done', 'Done')}</Button>
            </DialogOverlayActions>
          </DialogOverlayFooter>
        </DialogOverlay>
      </>
    )
  }
  const anyChecked = () => selectedRows?.length > 0

  return (
    <>
      {totalCompletedOrdinancesCount !== undefined && totalCompletedOrdinancesCount !== '' && (
        <Row>
          <span growX>
            {`${t('ordinances.users.totalCompleted', 'Total Completed Ordinances')}: ${totalCompletedOrdinancesCount}`}
          </span>
          {totalCompletedOrdinancesCount > 0 && (
            <Button
              variant="contained"
              emphasis="high"
              disabled={!isAuthorized('tss_AdminReadUserOrdinancePermission') || !anyChecked()}
              onClick={updateHiddenFromUserFlag}
            >
              {`${t('ordinances.users.toggleHiddenFromUser', "Toggle 'Hidden from User' Flag")}`}
            </Button>
          )}
          {failedCtrUpdates.length > 0 && failedUpdateOverlay()}
        </Row>
      )}
      {completedOrdinances && completedOrdinances.length > 0 && (
        <>
          <Separator />
          <UserOrdinancesResponsiveTable
            completedOrdinances={completedOrdinances}
            handleSelectedOrdinances={setSelectedRows}
          />
        </>
      )}
      {totalCompletedOrdinancesCount > 0 && <InfiniteScroller fetchMore={() => fetchUserCompletedOrdinances(offset)} />}
    </>
  )
}
